import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { X, Sparkles, ArrowLeft, Clock } from 'lucide-react';

export const AnnouncementBar = ({ campaign, onDismiss, onTrackClick, onTrackView }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  // Trigger Logic (Immediate, Delay, Scroll, Exit Intent)
  useEffect(() => {
    if (!campaign) {
      setIsVisible(false);
      return;
    }

    const trigger = campaign.triggerType || 'IMMEDIATE';
    const delaySec = campaign.delaySeconds || 0;
    const scrollPct = campaign.scrollPercentage || 25;

    let delayTimer = null;
    let autoCloseTimer = null;

    const showBar = () => {
      setIsVisible(true);
      onTrackView?.(campaign);

      // Auto close timer if set
      if (campaign.autoCloseSeconds && campaign.autoCloseSeconds > 0) {
        autoCloseTimer = setTimeout(() => {
          setIsVisible(false);
          onDismiss?.(campaign);
        }, campaign.autoCloseSeconds * 1000);
      }
    };

    if (trigger === 'IMMEDIATE') {
      showBar();
    } else if (trigger === 'DELAY') {
      delayTimer = setTimeout(showBar, Math.max(delaySec, 1) * 1000);
    } else if (trigger === 'SCROLL') {
      const handleScroll = () => {
        const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (scrollHeight > 0) {
          const percent = (window.scrollY / scrollHeight) * 100;
          if (percent >= scrollPct) {
            showBar();
            window.removeEventListener('scroll', handleScroll);
          }
        }
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        window.removeEventListener('scroll', handleScroll);
        if (delayTimer) clearTimeout(delayTimer);
        if (autoCloseTimer) clearTimeout(autoCloseTimer);
      };
    } else if (trigger === 'EXIT_INTENT') {
      const handleMouseLeave = (e) => {
        if (e.clientY <= 15) {
          showBar();
          document.removeEventListener('mouseleave', handleMouseLeave);
        }
      };
      document.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        document.removeEventListener('mouseleave', handleMouseLeave);
        if (delayTimer) clearTimeout(delayTimer);
        if (autoCloseTimer) clearTimeout(autoCloseTimer);
      };
    }

    return () => {
      if (delayTimer) clearTimeout(delayTimer);
      if (autoCloseTimer) clearTimeout(autoCloseTimer);
    };
  }, [campaign?.id, campaign?.triggerType, campaign?.delaySeconds, campaign?.scrollPercentage, campaign?.autoCloseSeconds]);

  // Live countdown timer calculation
  useEffect(() => {
    if (!campaign?.hasCountdown || !campaign?.endDate) {
      setTimeLeft(null);
      return;
    }

    const calculateTime = () => {
      const difference = new Date(campaign.endDate).getTime() - Date.now();
      if (difference <= 0) {
        setTimeLeft(null);
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((difference / 1000 / 60) % 60);

      setTimeLeft({
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
      });
    };

    calculateTime();
    const interval = setInterval(calculateTime, 60000);
    return () => clearInterval(interval);
  }, [campaign?.endDate, campaign?.hasCountdown]);

  if (!campaign || !isVisible) return null;

  const customBg = campaign.bgColor || '#0A235C';
  const customText = campaign.textColor || '#FFFFFF';
  const customBtnBg = campaign.buttonColor || '#13C5E9';
  const customBtnText = campaign.buttonTextColor || '#0A235C';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        style={{ backgroundColor: customBg, color: customText }}
        className="relative z-50 border-b border-white/10 shadow-sm overflow-hidden"
        dir="rtl"
      >
        <div className="max-w-[1536px] mx-auto px-4 py-2 sm:py-2.5 flex items-center justify-between gap-3 text-xs sm:text-sm">
          
          {/* Main Text Content */}
          <div className="flex-1 flex items-center justify-center flex-wrap gap-2 text-center">
            {campaign.badgeText && (
              <span className="inline-flex items-center gap-1 bg-white/20 text-white border border-white/30 font-bold px-2 py-0.5 rounded-full text-[11px] shadow-sm">
                <Sparkles className="w-3 h-3" />
                {campaign.badgeText}
              </span>
            )}

            <span className="font-bold truncate max-w-[500px]">
              {campaign.title}
            </span>

            {campaign.subtitle && (
              <span className="hidden md:inline opacity-80 text-xs">
                — {campaign.subtitle}
              </span>
            )}

            {/* Countdown Badge */}
            {timeLeft && (
              <span className="inline-flex items-center gap-1.5 bg-black/30 border border-white/10 text-white/90 text-xs font-mono px-2.5 py-0.5 rounded-md">
                <Clock className="w-3 h-3 text-[#13C5E9]" />
                <span className="text-[#FF8A1F] font-bold">{timeLeft.days}</span>ي :{' '}
                <span className="text-[#FF8A1F] font-bold">{timeLeft.hours}</span>س :{' '}
                <span className="text-[#FF8A1F] font-bold">{timeLeft.minutes}</span>د
              </span>
            )}

            {/* CTA Button / Link */}
            {campaign.ctaText && campaign.ctaUrl && (
              <Link
                to={campaign.ctaUrl}
                onClick={() => onTrackClick?.(campaign)}
                style={{ backgroundColor: customBtnBg, color: customBtnText }}
                className="inline-flex items-center gap-1 font-extrabold text-xs px-3 py-1 rounded-full shadow-sm hover:opacity-90 transition-all ml-1"
              >
                <span>{campaign.ctaText}</span>
                <ArrowLeft className="w-3 h-3" />
              </Link>
            )}
          </div>

          {/* Dismiss Button */}
          {campaign.dismissible && (
            <button
              onClick={() => {
                setIsVisible(false);
                onDismiss?.(campaign);
              }}
              aria-label="إغلاق الإعلان"
              className="p-1 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
